<template>
  <vx-card class="mt-base" :title="activityTitle">
    <p class="mb-5">This activity consists of two parts:</p>
    <ul class="numbered-list">
      <li>
        Learning some basics about electricity and electrical currents (and then answering some
        questions to test your knowledge)
      </li>
      <li>
        Putting this knowledge to use by playing a game where you have to create electrical circuits
      </li>
    </ul>
    <p class="mb-5">So, let’s get started.</p>

    <h4 class="mb-5">1. Electricity: The Basics</h4>
    <p class="mb-5">
      Watch the following video. In it you’ll learn how electricity works starting from the basics
      of the free electron in the atom, through conductors, voltage, current, resistors, led, to
      capacitors and transformers.
    </p>
    <responsive-youtube-wrapper :video="videos[1].video" :image="videos[1].image" />
    <h4 class="mb-5">Task 1:</h4>
    <p class="mb-5">
      Did you pay attention while watching the video? I hope so because now you have a series of
      questions to answer!
    </p>
    <p class="mb-5">
      Take the quiz below to find out how much you know about the very basics of electricity and how
      it works.
    </p>
    <div v-for="(question, index) in questions1" :key="'q_' + index">
      <h5 class="mb-5 questionlabel">{{ question.question }}</h5>
      <radio-buttons-wrapper
        v-model="data['question' + index]"
        :index="'q_' + index"
        :question="question"
        :is-editable="isEditable"
        show-question="false"
      />
    </div>

    <h4 class="mb-5 mt-5">2. Electrical Circuits</h4>
    <h4 class="mb-5 mt-5">Task 2:</h4>
    <p class="mb-5">
      Create electrical circuits using the “Wired” game (and upload a screenshot of the final game
      screen).
    </p>
    <p class="mb-5">
      Wire up electrical circuits and make your way through a derelict building to keep your
      appointment with a mysterious professor who lives at the top.
    </p>
    <p class="mb-5">
      Wired is an atmospheric puzzle-platformer set in a derelict building that has remained
      untouched since the 1970’s. In each room, you will wire up the lifts, doors, switches and fuel
      cells in order to get through. The puzzle mechanics are built on an accurate model of how
      electricity behaves. As you progress through the game, you will solve problems using your
      increasing knowledge of physics and how electricity behaves.
    </p>
    <p class="mb-5">
      Throughout the game, you encounter cine projector screens giving information you need in order
      to progress and slowly unravelling the mystery of who you are, why you are there and why the
      professor built this strange thing in the first place.
    </p>
    <p class="mb-5">
      To find out more about the game and how it works, watch the explanatory video below:
    </p>
    <responsive-youtube-wrapper :video="videos[2].video" :image="videos[2].image" />
    <p class="mb-5">
      Once you’re ready to play, follow the link to access this free game. Your progress will save
      automatically as you work your way through the game.
    </p>
    <button
      type="button"
      class="no-style img-click-button"
      aria-label="Open the Wired game"
      @click="openGamePopup()"
    >
      <img
        :src="require('@/assets/images/experiences/engineering/wired_access_button.jpg')"
        class="img-responsive m-auto mb-5"
        alt=""
      />
    </button>
    <p class="mb-5">
      When you complete the entire game, take a screenshot of the final scene and upload it via the
      button below.
    </p>
    <fileupload-button-immediate
      v-model="uploaded_files['uploaded_file_1']"
      :is-editable="isEditable"
      filetype="photo_doc"
      upload-btn-text="Upload “Wired” Screenshot"
    />

    <save-buttons :is-editable="isEditable" @save="save" />
  </vx-card>
</template>

<script>
import methods from '@/views/WorkExperiences/components/activity_js/methods';
import computed from '@/views/WorkExperiences/components/activity_js/computed';
import inject from '@/views/WorkExperiences/components/activity_js/inject';
import RadioButtonsWrapper from '../../components/ActivitiesBlocks/RadioButtonsWrapper';
import store from '../../../../store/store';
export default {
  data() {
    return {
      videos: {
        1: {
          image: require('@/assets/images/experiences/engineering/how_electricity_work_splash.jpg'),
          video: 'https://www.youtube.com/embed/mc979OhitAg',
        },
        2: {
          image: require('@/assets/images/experiences/engineering/wired_game_explanation_splash.jpg'),
          video: 'https://www.youtube.com/embed/ZXKOxcYuHag',
        },
      },
      questions1: {
        1: {
          question: '1. Materials that allow electricity to flow are called',
          answers: [
            { val: 1, answer: 'a) Conductors', correct: true },
            { val: 2, answer: 'b) Insulators', correct: false },
            { val: 3, answer: 'c) Capacitors', correct: false },
          ],
        },
        2: {
          question: '2. Materials that do not allow electricity to flow are known as',
          answers: [
            { val: 1, answer: 'a) Conductors', correct: false },
            { val: 2, answer: 'b) Insulators', correct: true },
            { val: 3, answer: 'c) Capacitors', correct: false },
          ],
        },
        3: {
          question: '3. Which of the following is a good insulator?',
          answers: [
            { val: 1, answer: 'a) Copper', correct: false },
            { val: 2, answer: 'b) Rubber', correct: true },
            { val: 3, answer: 'c) Iron', correct: false },
          ],
        },
        4: {
          question: '4. We call the route that electrons flow along',
          answers: [
            { val: 1, answer: 'a) The voltage', correct: false },
            { val: 2, answer: 'b) The current', correct: false },
            { val: 3, answer: 'c) The circuit', correct: true },
          ],
        },
        5: {
          question: '5. The pushing force of electrons is known as ',
          answers: [
            { val: 1, answer: 'a) The voltage', correct: true },
            { val: 2, answer: 'b) The current', correct: false },
            { val: 3, answer: 'c) The circuit', correct: false },
          ],
        },
        6: {
          question: '6. We call the flow of electrons',
          answers: [
            { val: 1, answer: 'a) The voltage', correct: false },
            { val: 2, answer: 'b) The current', correct: true },
            { val: 3, answer: 'c) The circuit', correct: false },
          ],
        },
        7: {
          question: '7. We measure the flow of electrons with a unit of measurement known as',
          answers: [
            { val: 1, answer: 'a) Resistance', correct: false },
            { val: 2, answer: 'b) Inductor', correct: false },
            { val: 3, answer: 'c) Amps', correct: true },
          ],
        },
        8: {
          question: '8. What do we call components that store electrical charge?',
          answers: [
            { val: 1, answer: 'a) Capacitors', correct: true },
            { val: 2, answer: 'b) Resistors', correct: false },
            { val: 3, answer: 'c) Transformers', correct: false },
          ],
        },
        9: {
          question:
            '9. Look at the definitions below. Which of these describes Alternating Current (AC)?',
          answers: [
            {
              val: 1,
              answer:
                'a) An electric current that reverses its direction many times a second at regular intervals, typically used in power supplies.',
              correct: true,
            },
            {
              val: 2,
              answer: 'b) An electric current that flows in one direction only.',
              correct: false,
            },
          ],
        },
        10: {
          question: '10. Which type of electricity is found in batteries?',
          answers: [
            { val: 1, answer: 'a) Alternating Current (AC)', correct: false },
            { val: 2, answer: 'b) Direct Current (DC)', correct: true },
          ],
        },
      },
      uploaded_files: {
        uploaded_file_1: '',
      },
    };
  },
  methods: {
    ...methods,
    openGamePopup() {
      store.dispatch('popup/showIframePopup', 'https://wiredthegame.com/entry.html');
    },
  },
  computed: computed,
  inject: inject,
  components: { RadioButtonsWrapper },
};
</script>
